<template>
	<v-textarea
		ref="textarea"
		:value="value"
		:auto-grow="autoGrow"
		:autofocus="autofocus"
		:background-color="backgroundColor"
		:clearable="clearable"
		:dense="dense"
		:disabled="disabled"
		:hide-details="hideDetails"
		:hint="hint"
		:label="label"
		:no-resize="noResize"
		:outlined="outlined"
		:placeholder="placeholder"
		:solo="solo"
		:rows="rows"
		v-bind="forwardedAttrs"
		v-on="$listeners"
	/>
</template>

<script>
export default {
	name: 'KTextarea',
	inheritAttrs: false,
	model: {
		prop: 'value',
		event: 'input'
	},
	props: {
		value: {
			type: [String, Number, Boolean],
			default: ''
		},
		autoGrow: {
			type: Boolean,
			default: false
		},
		autofocus: {
			type: Boolean,
			default: false
		},
		backgroundColor: {
			type: String,
			default: undefined
		},
		clearable: {
			type: Boolean,
			default: false
		},
		dense: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		hideDetails: {
			type: [Boolean, String],
			default: 'auto'
		},
		hint: {
			type: String,
			default: undefined
		},
		label: {
			type: String,
			default: undefined
		},
		noResize: {
			type: Boolean,
			default: false
		},
		outlined: {
			type: Boolean,
			default: false
		},
		placeholder: {
			type: String,
			default: undefined
		},
		rows: {
			type: [Number, String],
			default: 5
		},
		solo: {
			type: Boolean,
			default: false
		},
		accessibleLabel: {
			type: String,
			default: undefined
		}
	},
	computed: {
		forwardedAttrs() {
			const { tabindex, 'accessible-label': accessible_label, ...rest } = this.$attrs
			if (this.resolvedAriaLabel && !rest['aria-label'] && !rest['aria-labelledby']) {
				rest['aria-label'] = this.resolvedAriaLabel
			}
			return rest
		},
		resolvedAriaLabel() {
			return this.accessibleLabel || this.$attrs['aria-label']
		}
	},
	mounted() {
		this.sync_textarea_accessibility()
		this.sync_hidden_legend_accessibility()
	},
	updated() {
		this.sync_textarea_accessibility()
		this.sync_hidden_legend_accessibility()
	},
	methods: {
		sync_textarea_accessibility() {
			const root = this.$refs.textarea?.$el
			if (!root) return

			const textarea = root.querySelector('textarea')
			if (!textarea) return

			const tabindex = this.$attrs.tabindex
			if (tabindex !== undefined) textarea.setAttribute('tabindex', tabindex)

			if (this.resolvedAriaLabel) textarea.setAttribute('aria-label', this.resolvedAriaLabel)

			root.removeAttribute('tabindex')
		},
		sync_hidden_legend_accessibility() {
			const root = this.$refs.textarea?.$el
			if (!root) return

			const hidden_legend_span = root.querySelector('fieldset[aria-hidden="true"] legend span')
			if (!hidden_legend_span) return

			// Vuetify uses this span only to size the outlined notch. Keep the
			// layout behavior, but hide the decorative text so scanners do not
			// report contrast against a non-visible control artifact.
			hidden_legend_span.setAttribute('aria-hidden', 'true')
			hidden_legend_span.style.opacity = '0'
			hidden_legend_span.style.color = 'transparent'
			hidden_legend_span.style.backgroundColor = 'transparent'
		}
	}
};
</script>

<style lang="scss">
.v-application {
	.v-input.v-textarea.v-input--is-focused.v-text-field--outlined {
		.v-input__slot textarea:focus,
		.v-input__slot textarea:focus-visible {
			outline: none !important;
			box-shadow: none !important;
		}

		.v-input__slot fieldset {
			border: 2px solid #1565c0 !important;
		}
	}
}
</style>
